import { useState, useEffect, useCallback } from 'react';
import { Alert, Badge, Card, Space, Typography, Button, message, Drawer, Switch, Divider } from 'antd';
import { BellOutlined, CloseOutlined, SoundOutlined, SettingOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';

const { Text, Title } = Typography;

// Notification Types and their sounds
const NOTIFICATION_SOUNDS = {
  domain_registration: '/sounds/notification.mp3',
  payment_received: '/sounds/payment.mp3',
  domain_activated: '/sounds/notification.mp3',
  domain_expired: '/sounds/notification.mp3',
  support_ticket: '/sounds/notification.mp3',
  default: '/sounds/notification.mp3'
};

// Pre-load audio files
const audioElements = {};
Object.entries(NOTIFICATION_SOUNDS).forEach(([type, soundPath]) => {
  audioElements[type] = new Audio(soundPath);
  audioElements[type].load();
});

const AdminNotificationCenter = () => {
  const [notifications, setNotifications] = useState([]);
  const [connectionStatus, setConnectionStatus] = useState('disconnected');
  const [wsInstance, setWsInstance] = useState(null);
  const [notificationPermission, setNotificationPermission] = useState(Notification.permission);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [showDrawer, setShowDrawer] = useState(false);
  const [settings, setSettings] = useState({
    desktopNotifications: true,
    soundEnabled: true,
    autoHide: false,
    autoHideDelay: 5000
  });

  // Request notification permission
  const requestNotificationPermission = async () => {
    try {
      const permission = await Notification.requestPermission();
      setNotificationPermission(permission);
      if (permission === 'granted') {
        message.success('Desktop notifications enabled');
        setSettings(prev => ({ ...prev, desktopNotifications: true }));
      } else if (permission === 'denied') {
        message.error('Please enable notifications in your browser settings');
      }
    } catch (error) {
      console.error('Error requesting notification permission:', error);
    }
  };

  // Play notification sound
  const playNotificationSound = (notificationType) => {
    if (!settings.soundEnabled) return;

    try {
      const audio = audioElements[notificationType] || audioElements.default;
      audio.currentTime = 0;
      audio.play().catch(e => console.log('Audio playback prevented:', e));
    } catch (error) {
      console.error('Error playing notification sound:', error);
    }
  };

  const handleNotificationClick = (notification) => {
    switch (notification.type) {
      case 'domain_registration':
        window.location.href = `/admin/domains/${notification.data.domain}`;
        break;
      case 'payment_received':
        window.location.href = `/admin/payments/${notification.data.payment_id}`;
        break;
      case 'domain_activated':
        window.location.href = `/admin/domains/${notification.data.domain}`;
        break;
      case 'support_ticket':
        window.location.href = `/admin/support/${notification.data.ticket_id}`;
        break;
      default:
        window.location.href = '/admin/notifications';
    }
  };

  // Show desktop notification
  const showDesktopNotification = (notification) => {
    if (!settings.desktopNotifications || notificationPermission !== 'granted') return;

    try {
      const desktopNotif = new Notification(notification.title, {
        body: notification.body,
        icon: '/logo.png',
        tag: notification.type,
        requireInteraction: !settings.autoHide,
        silent: true // We handle sound separately
      });

      playNotificationSound(notification.type);

      desktopNotif.onclick = (event) => {
        window.focus();
        handleNotificationClick(notification);
        event.target.close();
      };

      if (settings.autoHide) {
        setTimeout(() => desktopNotif.close(), settings.autoHideDelay);
      }

    } catch (error) {
      console.error('Error showing desktop notification:', error);
    }
  };

  const connectWebSocket = useCallback(() => {
    try {
      const wsUrl = process.env.REACT_APP_API_URL?.replace('http://', 'ws://') + '/api/notifications/ws/admin';
      console.log('Connecting to WebSocket:', wsUrl);

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        setConnectionStatus('connected');
        message.success('Notification service connected');
        playNotificationSound('default');
      };

      ws.onmessage = (event) => {
        try {
          const notification = JSON.parse(event.data);
          
          setNotifications(prev => [{
            ...notification,
            id: Date.now(),
            isNew: true,
            timestamp: new Date().toISOString()
          }, ...prev].slice(0, 50));

          if (document.hidden || !document.hasFocus()) {
            showDesktopNotification(notification);
          } else {
            playNotificationSound(notification.type);
          }

        } catch (error) {
          console.error('Error processing message:', error);
        }
      };

      ws.onclose = (event) => {
        setConnectionStatus('disconnected');
        if (event.code !== 1000) {
          setTimeout(connectWebSocket, 5000);
        }
      };

      ws.onerror = (error) => {
        console.error('WebSocket error:', error);
        setConnectionStatus('error');
        message.error('Failed to connect to notification service');
      };

      setWsInstance(ws);
      return ws;
    } catch (error) {
      console.error('Error setting up WebSocket:', error);
      message.error('Failed to setup notification service');
      return null;
    }
  }, [settings]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden) {
        setNotifications(prev => 
          prev.map(n => ({...n, isNew: false}))
        );
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    const ws = connectWebSocket();

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      if (ws) {
        ws.close(1000, 'Component unmounting');
      }
    };
  }, [connectWebSocket]);

  const removeNotification = (notificationId) => {
    setNotifications(prev => 
      prev.filter(n => n.id !== notificationId)
    );
  };

  const clearAllNotifications = () => {
    setNotifications([]);
    message.success('All notifications cleared');
  };

  const getNotificationStyle = (type) => {
    switch (type) {
      case 'domain_registration':
        return { borderLeft: '4px solid #1890ff' };
      case 'payment_received':
        return { borderLeft: '4px solid #52c41a' };
      case 'domain_activated':
        return { borderLeft: '4px solid #722ed1' };
      case 'support_ticket':
        return { borderLeft: '4px solid #faad14' };
      default:
        return { borderLeft: '4px solid #d9d9d9' };
    }
  };

  return (
    <>
      <div style={{ 
        position: 'fixed', 
        right: 20, 
        top: 20, 
        width: 384, 
        zIndex: 1000 
      }}>
        {/* Settings Card */}
        <Card size="small" style={{ marginBottom: 8 }}>
          <Space align="center" style={{ width: '100%', justifyContent: 'space-between' }}>
            <Space>
              <Badge 
                status={
                  connectionStatus === 'connected' ? 'success' :
                  connectionStatus === 'error' ? 'error' : 'processing'
                } 
              />
              <Text type="secondary">
                {connectionStatus === 'connected' ? 'Connected' :
                 connectionStatus === 'error' ? 'Connection Error' : 'Reconnecting...'}
              </Text>
            </Space>

            <Space>
              <Button
                size="small"
                icon={<SoundOutlined />}
                type={settings.soundEnabled ? 'primary' : 'default'}
                onClick={() => setSettings(prev => ({ 
                  ...prev, 
                  soundEnabled: !prev.soundEnabled 
                }))}
              />
              <Button
                size="small"
                icon={<SettingOutlined />}
                onClick={() => setShowDrawer(true)}
              />
              {notificationPermission !== 'granted' && (
                <Button
                  size="small"
                  icon={<BellOutlined />}
                  onClick={requestNotificationPermission}
                >
                  Enable Notifications
                </Button>
              )}
            </Space>
          </Space>
        </Card>

        {/* Notifications List */}
        <div style={{ 
          maxHeight: '80vh', 
          overflowY: 'auto',
          display: 'flex',
          flexDirection: 'column',
          gap: 8
        }}>
          {notifications.length === 0 ? (
            <Card size="small">
              <Text type="secondary">No new notifications</Text>
            </Card>
          ) : (
            <>
              <Button 
                size="small" 
                type="text" 
                onClick={clearAllNotifications}
                style={{ alignSelf: 'flex-end' }}
              >
                Clear All
              </Button>
              {notifications.map(notification => (
                <Card
                  key={notification.id}
                  size="small"
                  style={{
                    ...getNotificationStyle(notification.type),
                    position: 'relative'
                  }}
                >
                  <Button
                    type="text"
                    icon={<CloseOutlined />}
                    size="small"
                    onClick={() => removeNotification(notification.id)}
                    style={{
                      position: 'absolute',
                      right: 8,
                      top: 8
                    }}
                  />

                  <div style={{ paddingRight: 24 }}>
                    <Text strong>{notification.title}</Text>
                    <div style={{ margin: '8px 0' }}>
                      {notification.body}
                    </div>

                    {notification.data && (
                      <div style={{ fontSize: 12, color: 'rgba(0, 0, 0, 0.45)' }}>
                        {notification.data.domain && (
                          <div>Domain: {notification.data.domain}</div>
                        )}
                        {notification.data.price && (
                          <div>Price: ₹{notification.data.price}</div>
                        )}
                        {notification.data.user && (
                          <div>
                            User: {notification.data.user.name || notification.data.user.email}
                          </div>
                        )}
                        <div>
                          {dayjs(notification.data.timestamp).format('MMM D, YYYY HH:mm:ss')}
                        </div>
                      </div>
                    )}

                    <div style={{ marginTop: 8 }}>
                      <Button
                        type="link"
                        size="small"
                        onClick={() => handleNotificationClick(notification)}
                      >
                        View Details
                      </Button>
                    </div>
                  </div>
                </Card>
              ))}
            </>
          )}
        </div>
      </div>

      {/* Settings Drawer */}
      <Drawer
        title="Notification Settings"
        placement="right"
        onClose={() => setShowDrawer(false)}
        open={showDrawer}
      >
        <Space direction="vertical" style={{ width: '100%' }}>
          <div>
            <Title level={5}>Desktop Notifications</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Switch
                  checked={settings.desktopNotifications}
                  onChange={(checked) => setSettings(prev => ({
                    ...prev,
                    desktopNotifications: checked
                  }))}
                />
                <Text style={{ marginLeft: 8 }}>Show Desktop Notifications</Text>
              </div>
              <div>
                <Switch
                  checked={settings.autoHide}
                  onChange={(checked) => setSettings(prev => ({
                    ...prev,
                    autoHide: checked
                  }))}
                />
                <Text style={{ marginLeft: 8 }}>Auto-hide Notifications</Text>
              </div>
            </Space>
          </div>

          <Divider />

          <div>
            <Title level={5}>Sound Settings</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              <div>
                <Switch
                  checked={settings.soundEnabled}
                  onChange={(checked) => setSettings(prev => ({
                    ...prev,
                    soundEnabled: checked
                  }))}
                />
                <Text style={{ marginLeft: 8 }}>Enable Notification Sounds</Text>
              </div>
            </Space>
          </div>

          <Divider />

          <div>
            <Title level={5}>Test Notifications</Title>
            <Space direction="vertical" style={{ width: '100%' }}>
              {Object.keys(NOTIFICATION_SOUNDS).map(type => (
                <Button
                  key={type}
                  onClick={() => playNotificationSound(type)}
                >
                  Test {type.replace('_', ' ')} sound
                </Button>
              ))}
            </Space>
          </div>
        </Space>
      </Drawer>
    </>
  );
};

export default AdminNotificationCenter;